import React, { useEffect, useState } from "react";
import queryString from 'query-string';
import { useLocation } from 'react-router-dom';
import CreditCardType from "credit-card-type"
import {FlexContainer, Spinner} from "../../design";
import TransactionModal from "../../components/modal/transaction";
import SuccessModal from "../../components/modal/success";
import { addIntent, getAccountInfo, getSessionAuth } from "../../api";
import {MESSAGES, SWIREPAY_URL, SETUP_SESSION_STATUSES} from "../../appconstants";
import BaseSplitPage from "../../components/splitpage";
import CardPayment from "../../components/cardpayment/CardPayment";
import Support from "../../components/support";
import CardIcon from "../../components/icons/CardIcon";
import PaymentMethodTabs from "../../components/paymentmethodtabs";

type Props = {
    history: {
        push: Function,
    },
}

const SetUpSession = (props:Props) => {

    const location = useLocation();
    const parsed = queryString.parse(location.search);
    const publicKey= parsed.key;
    const redirectUrl=parsed.redirectUrl;
    const seesionId=parsed.ss;
    const { pathname } = location;
    const [accountDetails, setAccountDetails] = useState(null);
    const [isTransactionModalOpen, setTransactionModalOpen] = useState(false);
    const [isTransactionSuccessModalOpen, setTransactionSuccessModalOpen] = useState(false);
    const [payment, setPayment] = useState(null);
    const [errorMessage, setErrorMessage] = useState();
    const [isShowSpinner, setShowSpinner] = useState(false);
    const [errorHeader, setErrorHeader] = useState("Error occurred while creating session.");
    const [customer, setCustomer] = useState(null);
    const [redirectUrlPath, setRedirectUrlPath] = useState('');

    const [ipAddress, setIpAddress] = useState('');

    const getData = async () => {
        const response = await fetch('https://api.ipify.org/?format=json');
        const data = await response.json();
        setIpAddress(data.ip);
      }
    
      useEffect(() => {
        getData();
      }, []);

    useEffect(() => {
        if (redirectUrl) {
            setRedirectUrlPath(redirectUrl);
        } else {
            setRedirectUrlPath(SWIREPAY_URL);
        }
        if(publicKey && publicKey.length===56 && !seesionId) {
            getAccountDetailsWithGid();
        } else if(publicKey && publicKey.length===56 && seesionId && customer) {
            getAccountDetailsWithGid();
        } else if (seesionId && !customer) {
            getAuthentication();
        } else {
            props.history.push(`${pathname}error`);
        }
    }, []);

    const getAccountDetailsWithGid = async () => {
        const config = {
            headers: {
                'x-api-key': atob(publicKey),
            }
        };
        setShowSpinner(true);
        try {
            const value = await getAccountInfo(config);
            if(value) {
                setAccountDetails(value)
            }
        } catch (reason) {
            props.history.push(`${pathname}error`);
            error(reason);
        } finally {
            setShowSpinner(false);
        }
    };

    const getAuthentication = async () => {
        setTransactionModalOpen(true);
        const config = {
            headers: {
                'x-api-key': atob(publicKey),
            }
        };
        try {
            const value = await getSessionAuth(config, seesionId);
            if(value) {
                setCustomer(value.customer);
                const { redirectUri, status } = value;
                if (SETUP_SESSION_STATUSES.VERIFIED === status) {
                    setTransactionModalOpen(false);
                    setTransactionSuccessModalOpen(true);
                    setTimeout(function () {
                        const parsed = queryString.parse(location.search);
                        let pathname = parsed.redirectUrl ? parsed.redirectUrl : SWIREPAY_URL;
                        if (pathname === "undefined") {
                            pathname=SWIREPAY_URL;
                        }
                        const redirectUrl = redirectUri ? redirectUri : pathname;
                        const urlPath = redirectUrl.includes("?");
                        if (urlPath) {
                            window.location = `${redirectUrl}&sp-session-id=${value.gid}`;
                        } else if (!urlPath) {
                            window.location = `${redirectUrl}?sp-session-id=${value.gid}`;
                        }
                    }, 5000);
                } else {
                    errorText(value);
                }
            }
        } catch (e) {
            props.history.push(`${pathname}error`);
        } finally {
            setShowSpinner(false);
        }
    }

    const errorText = data => {
        const { errorCode, errorDescription, status } = data;
        setErrorMessage(errorDescription);
        setErrorHeader("Authentication Required")
        if (status === 500) {
            setErrorMessage(MESSAGES.SETUP_INTENT.ERROR);
        }
        setTransactionModalOpen(false);
        const errorEl = document.getElementById("error");
        if (null !== errorEl) { errorEl.scrollIntoView(); }
    }

    const error = reason => {
        setErrorMessage(null);
        const { data: { message }, status } = reason;
        setErrorMessage(message);
        if (status === 500) {
            setErrorMessage(MESSAGES.SETUP_INTENT.ERROR);
        }
        setTransactionModalOpen(false);
        const errorEl = document.getElementById("error");
        if (null !== errorEl) { errorEl.scrollIntoView(); }
    };

    const getCardType = (number: String) => {
        const cardDetails = CreditCardType(number);
        let cardBrand = null;
        if(cardDetails.length > 0) {
            const card = cardDetails && cardDetails[0];
            cardBrand = card.type.toLocaleUpperCase();
        }
        return cardBrand;
    };

    const setUpIntent = async (data) => {
        setTransactionModalOpen(true);
        setCustomer(data.customer);
        const cardType = getCardType(data.paymentMethod.card.number)
        data.paymentMethod.card.scheme = cardType;
        const userAgent = {
            acceptHeaders: "Accept: text/html, application/xhtml+xml, application/xml;q=0.9, image/webp, */*;q=0.8", 
            ip: ipAddress,
            language: window.navigator.language, 
            colorDepth: window.screen.colorDepth,
            screenHeight: window.screen.height, 
            screenWidth: window.screen.width,
            timeZone: new Date().getTimezoneOffset(), 
            browserUserAgent: window.navigator.userAgent
           };
           data.paymentMethod.userAgent = userAgent;
        const returnUrl = `${window.location.origin}${pathname}?key=${publicKey}&redirectUrl=${redirectUrl}`;
        const params = {
            customer: data.customer,
            paymentMethod: data.paymentMethod,
            returnUrl: returnUrl,
        }
        const config = {
            headers: {
                'x-api-key': atob(publicKey),
            }
        };
        try {
            const value = await addIntent(params, config);
            if (value) {
                const { status } = value;
                setTransactionModalOpen(true);
                if (SETUP_SESSION_STATUSES.VERIFIED === status) {
                    setTransactionSuccessModalOpen(true);
                }
                setTimeout(function () {
                    const redirectUri = value.nextActionUrl ? value.nextActionUrl : redirectUrlPath;
                    const urlPath = redirectUri.includes("?");
                    if (urlPath) {
                        window.location = `${redirectUri}&sp-session-id=${value.gid}`;
                    } else if (!urlPath) {
                        window.location = `${redirectUri}?sp-session-id=${value.gid}`;
                    }
                }, 5000);
            }
        } catch (reason) {
            error(reason);
        } finally {
            setShowSpinner(false)
        }
    }

    const tabs = [
        {
            icon: <CardIcon />,
            name: "CARD",
            disabled: false
        }
    ];

    const tabPanels = [
        {
            children: <CardPayment
                errorMsg={errorMessage}
                errorHeader={errorHeader}
                customerReadOnly={false}
                submit={setUpIntent}
                headerText={"SetUp Intent"}
                buttonText={"Create"}
                customerValidation={true}
                preFillCustomer={customer}
                showAddress={false}
                alpha2={accountDetails && accountDetails.country && accountDetails.country.alpha2}
            />
        },
    ];

    return (
      <BaseSplitPage left={
          <>
              <FlexContainer
                flexDirection="column"
                position="relative"
                display="block"
                height="100%"
              >
                  <div style={{position: "sticky", top: "85%"}}>
                      <Support email={payment && payment.support && payment.support.email}
                               phone={payment && payment.support && payment.support.phone}/>
                  </div>
              </FlexContainer>
          </>
      } right={
          <>
              <PaymentMethodTabs paymentTypes={accountDetails && accountDetails.country && accountDetails.country.alpha2} tabs={tabs} tabPanels={tabPanels} />
              <TransactionModal body={null} header={null} isOpen={isTransactionModalOpen} />
              <SuccessModal isOpen={isTransactionSuccessModalOpen} successMsg={"Success"}/>
              <Spinner isOpen={isShowSpinner}/>
          </>
      }/>
    )
};

export default SetUpSession;
